import { useState, useRef } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, ChevronRight, Home, LogOut } from "lucide-react"; // Added more icons
import { Link, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/contexts/auth-context";

// Helper function to generate breadcrumbs (remains the same)
const generateBreadcrumbs = (pathname: string) => {
  const pathParts = pathname.split("/").filter((part) => part);
  const breadcrumbs = [
    { href: "/", label: "Home", icon: Home, isCurrent: pathname === "/" },
  ];

  let currentPath = "";
  pathParts.forEach((part, index) => {
    currentPath += `/${part}`;
    breadcrumbs.push({
      href: currentPath,
      label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " "),
      icon: Home, // Use Home as default icon
      isCurrent: index === pathParts.length - 1,
    });
  });
  return breadcrumbs;
};

export default function Header() {
  const location = useLocation();
  const pathname = location.pathname;
  const breadcrumbs = generateBreadcrumbs(pathname);
  const { user, logout } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const searchContainerRef = useRef<HTMLDivElement>(null);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 shrink-0">
      <div className="md:hidden">
        <SidebarTrigger className="h-8 w-8" />
      </div>
      <nav className="hidden md:flex items-center text-sm">
        {breadcrumbs.map((crumb, index) => (
          <div key={index} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 mx-1.5 text-muted-foreground" />
            )}
            {crumb.isCurrent ? (
              <span className="font-medium text-foreground">
                {index === 0 ? (
                  <span className="flex items-center gap-1.5">
                    <crumb.icon className="h-4 w-4" /> {crumb.label}
                  </span>
                ) : (
                  crumb.label
                )}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              >
                {crumb.icon && index === 0 && (
                  <crumb.icon className="h-4 w-4" />
                )}
                {crumb.label}
              </Link>
            )}
          </div>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-2 md:gap-3">
        <div className="relative hidden md:block" ref={searchContainerRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          <Input
            type="search"
            placeholder="Smart search..."
            className="pl-9 sm:w-[200px] md:w-[250px] lg:w-[300px] h-9 rounded-md bg-muted/50 focus-visible:ring-offset-0 focus-visible:ring-1"
            value={searchQuery}
            disabled
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="relative h-9 w-9 rounded-full p-0"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage
                  src="/placeholder.svg?width=40&height=40"
                  alt="User Profile"
                />
                <AvatarFallback>
                  {user
                    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                    : "UA"}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-medium">
                {user ? `${user.firstName} ${user.lastName}` : "User"}
              </p>
              <p className="text-xs text-muted-foreground">
                {user ? user.email : "user@example.com"}
              </p>
              {user && (
                <p className="text-xs text-muted-foreground">
                  Role: {user.roleName}
                </p>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
